import api from '@api/api';
import { serializeSearchParams } from '@api/searchParams';
import type { ListQuery } from '@models/listQuery';
import { Lookup } from '@models/lookup';
import { PagedResult } from '@models/pagedResult';
import { createCache, createResourceGroup } from 'cross-state';
import { ZodType } from 'zod';

export default abstract class CrudApi<T extends { id: string }> {
  abstract readonly path: string;
  abstract readonly model: ZodType<T, any, any>;
  readonly caches = createResourceGroup();

  list = createCache(
    async (query?: ListQuery) => {
      const searchParams = serializeSearchParams(query);
      const res = await api.get(this.path, { searchParams }).json();
      const items = PagedResult<T>(this.model).parse(res);

      for (const item of items.items) {
        const { status, isUpdating } = this.get(item.id).state.get();
        if (status === 'pending' && !isUpdating) {
          this.get(item.id).updateValue(item);
          this.get(item.id).invalidate();
        }
      }

      return items;
    },
    { resourceGroup: this.caches },
  );

  all = createCache(
    async (query?: Omit<ListQuery, 'skip' | 'limit'>) => {
      let skip = 0;
      const limit = 100;
      const items: T[] = [];
      let res;

      do {
        res = await this.list({ ...query, skip, limit }).get();
        items.push(...res.items);
        skip += limit;
      } while (res.hasMore);

      return items;
    },
    {
      resourceGroup: this.caches,
    },
  );

  lookup = this.all.mapCache((x) => Lookup.from(x));

  get = createCache(
    async (id: string) => {
      const res = await api.get(`${this.path}/${id}`).json();
      return this.model.parse(res);
    },
    {
      resourceGroup: this.caches,
    },
  );

  async create(item: T) {
    const res = await api.post(this.path, { json: item }).json();
    const updated = this.model.parse(res);

    this.list.invalidateAll();
    this.all.invalidateAll();
    this.get(updated.id).updateValue(updated);

    return updated;
  }

  async update(item: Partial<T>) {
    const res = await api.put(`${this.path}/${item.id}`, { json: item }).json();
    const updated = this.model.parse(res);

    this.list.invalidateAll();
    this.all.invalidateAll();
    this.get(updated.id).updateValue(updated);

    return updated;
  }

  async delete(id: string) {
    await api.delete(`${this.path}/${id}`);

    this.caches.invalidateAll();

    setTimeout(() => {
      this.get(id).clear();
    }, 1000);
  }
}
